/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { CircularProgress } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import type { InitialMapProps } from '@opengeoweb/core';
import { AlertBanner } from '@opengeoweb/shared';
import { MapViewCapConnect } from './MapViewCapConnect';
import { useGetConfig } from '../../utils/cap-api/hooks';
import { useCapTranslation } from '../../utils/i18n';

interface MapViewCapConnectWrapperProps {
  id?: string;
  title?: string;
  bbox?: { left: number; bottom: number; right: number; top: number };
  srs?: string;
  productConfigKey: string;
  layers?: InitialMapProps['mapPreset']['layers'];
}

export const MapViewCapConnectWrapper: React.FC<
  MapViewCapConnectWrapperProps
> = ({ id, title, bbox, srs, productConfigKey, layers }) => {
  const { t } = useCapTranslation();

  const {
    data: capWarningPresets,
    isPending,
    error,
  } = useGetConfig(productConfigKey);

  if (isPending) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress data-testid="loadingSpinner" />
      </Box>
    );
  }
  if (error) {
    return (
      <AlertBanner info={error.message} title={t('map-view-cap-error-title')} />
    );
  }

  return (
    <MapViewCapConnect
      bbox={bbox}
      capWarningPresets={capWarningPresets}
      id={id}
      layers={layers}
      srs={srs}
      title={title}
    />
  );
};
